import { Trophy, X } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { useCountUp } from '../../hooks/useCountUp';
import { useJackpotEvents, type JackpotEvent } from '../../hooks/useJackpotEvents';
import {
  jackpotService,
  type JackpotGameView,
  type JackpotView,
} from '../../services/jackpot.service';
import { formatMoney, formatRelativeTime } from '../../utils/format';

const OPEN_KEY = 'jackpot-widget-open';
const POS_KEY = 'jackpot-widget-pos';
/** Nhịp làm tươi "nổ X trước" (giờ/phút trôi) — 30s là đủ mượt, không tốn. */
const RELATIVE_TICK_MS = 30_000;
/** Thời gian giữ hiệu ứng flash sau khi một hũ nổ. */
const FLASH_MS = 1_600;

/** Màu + nhãn chip theo game (gid cố định từ observer). Game lạ → mặc định accent. */
const STYLE: Record<number, { text: string; chip: string; label: string }> = {
  1: { text: 'text-warning', chip: 'bg-warning text-canvas', label: 'TX' },
  2: { text: 'text-success', chip: 'bg-success text-canvas', label: 'XĐ' },
  3: { text: 'text-accent', chip: 'bg-accent text-canvas', label: 'M5' },
};
const FALLBACK_STYLE = { text: 'text-accent', chip: 'bg-accent text-canvas', label: '★' };
const styleFor = (gid: number) => STYLE[gid] ?? FALLBACK_STYLE;

function readOpen(): boolean {
  try {
    return localStorage.getItem(OPEN_KEY) !== 'false';
  } catch {
    return true;
  }
}
/** Kích thước sticker (để đặt mặc định ở góc + kẹp trong màn hình). */
const STICKER_SIZE = 56;
const EDGE_MARGIN = 8;

function readPos(): { x: number; y: number } {
  try {
    const raw = localStorage.getItem(POS_KEY);
    if (raw) return JSON.parse(raw) as { x: number; y: number };
  } catch {
    // bỏ qua — dùng mặc định
  }
  // Mặc định: góc dưới-phải (chỗ quen thuộc của nút nổi).
  return {
    x: window.innerWidth - STICKER_SIZE - 20,
    y: window.innerHeight - STICKER_SIZE - 20,
  };
}

/** Kẹp toạ độ (top-left) để phần tử `w×h` nằm gọn trong màn hình. */
function clampIntoView(px: number, py: number, w: number, h: number): { x: number; y: number } {
  const maxX = Math.max(EDGE_MARGIN, window.innerWidth - w - EDGE_MARGIN);
  const maxY = Math.max(EDGE_MARGIN, window.innerHeight - h - EDGE_MARGIN);
  return {
    x: Math.min(Math.max(EDGE_MARGIN, px), maxX),
    y: Math.min(Math.max(EDGE_MARGIN, py), maxY),
  };
}

/** Số hũ, chạy mượt từ giá trị cũ → mới. */
function Pot({ value, className }: { value: number; className: string }) {
  const shown = useCountUp(value, 700);
  return (
    <span className={className}>
      {formatMoney(shown)} <span className="text-ink-faint text-[9px] tracking-wider">VND</span>
    </span>
  );
}

/**
 * Widget báo hũ nổi: sticker tròn (ghim góc) → popup kéo-thả hiển thị hũ 3
 * game realtime + "nổ bao lâu trước". Mount 1 lần trong AppLayout (mọi trang đã
 * đăng nhập). Ẩn hoàn toàn khi chưa cấu hình báo hũ (API trả `games: []`).
 */
export function JackpotWidget() {
  const [view, setView] = useState<JackpotView | null>(null);
  const [open, setOpen] = useState(readOpen);
  const [flashGid, setFlashGid] = useState<number | null>(null);
  const [, forceTick] = useState(0);

  // Vị trí popup (top-left tuyệt đối, áp bằng CSS left/top — KHÔNG dùng transform
  // của framer để tránh nhảy). Nhớ qua localStorage.
  const [pos, setPos] = useState(readPos);
  const popupRef = useRef<HTMLDivElement>(null);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dragCleanup = useRef<(() => void) | null>(null);

  // Chỉ chạy nền (SSE realtime + nhịp làm tươi giờ) khi THỰC SỰ có hũ để hiện.
  // Chưa cấu hình báo hũ / lỗi fetch → view rỗng → widget ẩn, không giữ kết nối.
  const active = (view?.games.length ?? 0) > 0;

  // Ảnh chụp ban đầu (mở app). Lỗi/chưa cấu hình → view rỗng → widget ẩn.
  useEffect(() => {
    let cancelled = false;
    void jackpotService
      .getState()
      .then((data) => {
        if (!cancelled) setView(data);
      })
      .catch(() => {
        if (!cancelled) setView({ status: 'disconnected', updatedAt: null, games: [] });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Realtime: `state` cập nhật giá trị (giữ lastHitAt cũ); `win` set mốc nổ + flash.
  //
  // Phân loại TƯỜNG MINH từng `type`, KHÔNG dùng `else` bắt tất. Channel này còn
  // chở frame `rounds` (gói ván 4 game), và nó tới ngay vài ms sau mỗi `win` —
  // cùng `emitWin` → `captureFromEvent` → `syncRoundsToRedis`. Rơi vào nhánh `win`
  // thì `gid` là `undefined`: flash 1.6 giây bị tắt ngay lúc hũ vừa nổ, và suốt
  // chuyến bay Máy bay (2 frame `rounds`/giây) widget dựng lại mảng games rồi
  // re-render 2 lần/giây trên MỌI trang. Frame `rounds` là việc của sảnh, ở đây
  // không đụng gì tới nó — không setState, không re-render.
  useJackpotEvents((event: JackpotEvent) => {
    if (event.type === 'state') {
      setView((prev) => mergeState(prev, event.games, event.status, event.updatedAt));
    } else if (event.type === 'win') {
      setView((prev) => applyWin(prev, event.gid, event.value, event.at));
      setFlashGid(event.gid);
      if (flashTimer.current) clearTimeout(flashTimer.current);
      flashTimer.current = setTimeout(() => setFlashGid(null), FLASH_MS);
    }
  }, active);

  // Làm tươi "nổ X trước" theo thời gian trôi — chỉ khi widget đang hiện.
  useEffect(() => {
    if (!active) return;
    const timer = setInterval(() => forceTick((n) => n + 1), RELATIVE_TICK_MS);
    return () => clearInterval(timer);
  }, [active]);

  useEffect(() => {
    try {
      localStorage.setItem(OPEN_KEY, String(open));
    } catch {
      // bỏ qua
    }
  }, [open]);

  // Dọn khi unmount: timer flash + listener kéo còn treo (unmount giữa lúc đang kéo).
  useEffect(() => {
    return () => {
      if (flashTimer.current) clearTimeout(flashTimer.current);
      dragCleanup.current?.();
    };
  }, []);

  const savePos = (p: { x: number; y: number }): void => {
    try {
      localStorage.setItem(POS_KEY, JSON.stringify(p));
    } catch {
      // bỏ qua
    }
  };

  // Kéo popup bằng pointer THỦ CÔNG (không dùng framer drag — nó đánh nhau với
  // x/y có kiểm soát + dragConstraints trên phần tử `fixed` gây nhảy vị trí). Chỉ
  // khởi động từ header; kẹp trong màn hình; lưu localStorage khi thả.
  const startDrag = (e: React.PointerEvent): void => {
    const el = popupRef.current;
    const w = el?.offsetWidth ?? 400;
    const h = el?.offsetHeight ?? 300;
    const offX = e.clientX - pos.x;
    const offY = e.clientY - pos.y;
    let latest = pos;

    const move = (ev: PointerEvent): void => {
      latest = clampIntoView(ev.clientX - offX, ev.clientY - offY, w, h);
      setPos(latest);
    };
    const end = (): void => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
      dragCleanup.current = null;
      savePos(latest);
    };
    const stop = (): void => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
      dragCleanup.current = null;
    };
    dragCleanup.current = stop; // để effect unmount gỡ nếu đang kéo dở
    window.addEventListener('pointermove', move);
    // touch/pen có thể phát `pointercancel` thay `pointerup` (gesture/OS ngắt) →
    // nghe cả hai, nếu không listener treo làm kéo "kẹt" + leak.
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
  };

  // Khi MỞ: popup 400px có thể tràn nếu vị trí cũ sát mép — kẹp lại cho gọn trong
  // màn hình (đo kích thước thật sau render).
  useLayoutEffect(() => {
    if (!open) return;
    const el = popupRef.current;
    if (!el) return;
    const c = clampIntoView(pos.x, pos.y, el.offsetWidth, el.offsetHeight);
    setPos(c);
    savePos(c);
    // Chỉ kẹp khi MỞ; cố ý KHÔNG phụ thuộc `pos` để không chạy lại mỗi lần kéo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const games = view?.games ?? [];
  if (games.length === 0) return null; // chưa có dữ liệu / chưa cấu hình → ẩn hẳn

  // ── thu gọn: sticker (GHIM cố định góc phải, không kéo) ──
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Mở báo hũ"
        className="from-warning fixed bottom-5 right-5 z-[60] grid h-14 w-14 place-items-center rounded-full bg-gradient-to-br to-yellow-600 text-yellow-950 shadow-lg shadow-yellow-500/30 transition-transform hover:scale-105"
      >
        <Trophy size={24} />
        <span className="bg-danger border-canvas absolute right-1 top-1 h-3 w-3 rounded-full border-2" />
      </button>
    );
  }

  // ── mở: popup kéo-thả (pointer thủ công) ──
  return (
    <div
      ref={popupRef}
      style={{ left: pos.x, top: pos.y }}
      className="border-line bg-panel fixed z-[60] w-[400px] max-w-[calc(100vw-1.5rem)] overflow-hidden rounded-2xl border shadow-2xl"
    >
      {/* Header = tay cầm kéo (chỉ kéo popup từ đây, không phải cả thân) */}
      <div
        onPointerDown={startDrag}
        className="border-line from-warning/10 flex cursor-grab touch-none items-center gap-2 border-b bg-gradient-to-r to-transparent px-4 py-3 active:cursor-grabbing"
      >
        <span className="bg-success h-2 w-2 animate-pulse rounded-full" />
        <span className="text-ink text-sm font-bold tracking-wide">JACKPOT LIVE</span>
        <span className="text-ink-faint ml-auto select-none text-xs tracking-widest">⠿</span>
        <button
          type="button"
          onClick={() => setOpen(false)}
          onPointerDown={(e) => e.stopPropagation()}
          aria-label="Đóng, thu về sticker"
          className="border-line bg-canvas-raised text-ink-dim hover:bg-danger grid h-6 w-6 place-items-center rounded-md border hover:text-white"
        >
          <X size={14} />
        </button>
      </div>

      {/* Rows */}
      <div className="flex flex-col gap-1.5 p-2">
        {games.map((g) => (
          <JackpotRow key={g.gid} game={g} flash={flashGid === g.gid} />
        ))}
      </div>

      {/* Footer */}
      <div className="border-line-soft text-ink-faint flex items-center justify-between border-t px-4 py-2 text-[10px]">
        <span>cập nhật {view?.updatedAt ? formatRelativeTime(view.updatedAt) : '—'}</span>
        <span className="text-success font-semibold tracking-wide">● LIVE</span>
      </div>
    </div>
  );
}

function JackpotRow({ game, flash }: { game: JackpotGameView; flash: boolean }) {
  const s = styleFor(game.gid);
  return (
    <div
      className={`bg-canvas-raised relative grid grid-cols-[40px_1fr_auto] items-center gap-3 rounded-xl border px-3 py-2.5 transition-colors duration-500 ${
        flash ? 'border-warning bg-warning/10' : 'border-line-soft'
      }`}
    >
      <div className={`grid h-10 w-10 place-items-center rounded-lg text-xs font-bold ${s.chip}`}>
        {s.label}
      </div>
      <div className="min-w-0">
        <div className="text-ink truncate text-[13px] font-semibold">
          {game.name ?? `Game ${String(game.gid)}`}
        </div>
        <div className="text-ink-faint font-mono text-[11px]">
          {game.lastHitAt ? (
            <>
              nổ{' '}
              <span className="text-ink-dim font-semibold">
                {formatRelativeTime(game.lastHitAt)}
              </span>
            </>
          ) : (
            'chưa ghi nhận nổ'
          )}
        </div>
      </div>
      <Pot
        value={game.jackpot}
        className={`text-right font-mono text-[14px] font-bold tabular-nums ${s.text}`}
      />
    </div>
  );
}

// ── merge helpers (thuần) ──

/** Cập nhật giá trị hũ từ event `state`, GIỮ `lastHitAt`/`lastValue` đã biết. */
function mergeState(
  prev: JackpotView | null,
  games: { gid: number; name?: string; jackpot: number }[],
  status: JackpotView['status'],
  updatedAt: string,
): JackpotView {
  const hitByGid = new Map((prev?.games ?? []).map((g) => [g.gid, g]));
  return {
    status,
    updatedAt,
    games: games.map((g) => {
      const old = hitByGid.get(g.gid);
      return {
        gid: g.gid,
        ...(g.name ? { name: g.name } : old?.name ? { name: old.name } : {}),
        jackpot: g.jackpot,
        ...(old?.lastHitAt ? { lastHitAt: old.lastHitAt, lastValue: old.lastValue } : {}),
      };
    }),
  };
}

/** Ghi mốc nổ mới cho một hũ (từ event `win`). */
function applyWin(
  prev: JackpotView | null,
  gid: number,
  value: number,
  at: number,
): JackpotView | null {
  if (!prev) return prev;
  return {
    ...prev,
    games: prev.games.map((g) =>
      g.gid === gid ? { ...g, lastHitAt: new Date(at).toISOString(), lastValue: value } : g,
    ),
  };
}
